import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Copy,
  Check,
  ExternalLink,
  Music,
  Tag,
  Save,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  Wrench
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = "https://profile-sync-error.emergent.host";

// Available mood tags
const MOOD_OPTIONS = [
  { id: "peaceful", label: "Peaceful", emoji: "🍃" },
  { id: "happy", label: "Happy", emoji: "😊" },
  { id: "loving", label: "Loving", emoji: "💕" },
  { id: "cozy", label: "Cozy", emoji: "🧸" },
  { id: "grateful", label: "Grateful", emoji: "🙏" },
  { id: "relaxed", label: "Relaxed", emoji: "🌊" },
  { id: "energetic", label: "Energetic", emoji: "🚀" },
  { id: "playful", label: "Playful", emoji: "🎈" },
  { id: "gentle", label: "Gentle", emoji: "🌷" },
  { id: "dreamy", label: "Dreamy", emoji: "✨" },
  { id: "uplifting", label: "Uplifting", emoji: "🌟" },
  { id: "comfort", label: "Comfort", emoji: "🤗" },
  { id: "singalong", label: "Sing-along", emoji: "🎤" },
  { id: "instrumental", label: "Instrumental", emoji: "🎹" },
  { id: "memorial", label: "Memorial", emoji: "🌈" },
  { id: "big-bear-eagles", label: "Big Bear Eagles", emoji: "🦅" },
];

// Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('pawsToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const SongManagerPage = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedSong, setExpandedSong] = useState(null);
  const [editingMoods, setEditingMoods] = useState({});
  const [saving, setSaving] = useState(null);
  const [filter, setFilter] = useState("all"); // all, with-moods, without-moods
  const [repairing, setRepairing] = useState(false);

  // Fetch songs
  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API}/songs?limit=500`, { headers: getAuthHeader() });
      setSongs(response.data);
    } catch (e) {
      console.error("Failed to fetch songs:", e);
      toast.error("Failed to load songs");
    } finally {
      setLoading(false);
    }
  };

  // Repair audio links - matches GridFS files to songs by filename
  const repairAudioLinks = async () => {
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/songs/repair-audio-links`, {}, { headers: getAuthHeader() });
      const data = response.data;
      
      if (data.error) {
        // Something went wrong but we got info back
        toast.error(data.message || "Repair encountered an issue");
        console.log("Repair response:", data);
      } else if (data.repaired_count > 0) {
        toast.success(`Repaired ${data.repaired_count} songs!`);
      } else if (data.gridfs_files_found === 0) {
        toast.info("No GridFS audio files found - audio may be stored inline");
      } else {
        toast.info("No songs needed repair - all audio links are intact");
      }
      
      if (data.not_found_count > 0) {
        toast.info(`${data.not_found_count} songs have no matching audio file`);
      }
      
      // Refresh the song list
      await fetchSongs();
    } catch (e) {
      console.error("Failed to repair audio links:", e);
      const errorMsg = e.response?.data?.detail || "Failed to repair audio links";
      toast.error(errorMsg);
    } finally {
      setRepairing(false);
    }
  };

  // Repair the specific 9 songs with known audio IDs from Emergent support
  const repairNineSongs = async () => {
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/songs/repair-nine-songs`, {}, { headers: getAuthHeader() });
      const data = response.data;
      
      toast.success(data.message);
      console.log("Repair results:", data.results);
      
      // Show individual results
      data.results.forEach(r => {
        if (r.status === "REPAIRED") {
          toast.success(`✓ ${r.title}`);
        } else {
          toast.error(`✗ ${r.title} - not found`);
        }
      });
      
      // Refresh the song list
      await fetchSongs();
    } catch (e) {
      console.error("Failed to repair nine songs:", e);
      const errorMsg = e.response?.data?.detail || "Failed to repair songs";
      toast.error(errorMsg);
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    fetchSongs();
  }, []);

  // Copy URL to clipboard
  const copyUrl = (songId) => {
    const url = `${SITE_URL}/?song=${songId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(songId);
      toast.success("URL copied!");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Copy all URLs
  const copyAllUrls = () => {
    const urls = songs.map(s => `${s.title}\n${SITE_URL}/?song=${s.id}`).join("\n\n");
    navigator.clipboard.writeText(urls).then(() => {
      toast.success(`Copied ${songs.length} song URLs!`);
    });
  };

  // Toggle mood for a song
  const toggleMood = (songId, mood) => {
    setEditingMoods(prev => {
      const current = prev[songId] || songs.find(s => s.id === songId)?.moods || [];
      const newMoods = current.includes(mood)
        ? current.filter(m => m !== mood)
        : [...current, mood];
      return { ...prev, [songId]: newMoods };
    });
  };

  // Save moods for a song
  const saveMoods = async (songId) => {
    setSaving(songId);
    try {
      const moods = editingMoods[songId] || [];
      const song = songs.find(s => s.id === songId);
      
      // Include title (required by backend) along with moods
      await axios.put(`${API}/songs/${songId}`, { 
        title: song.title,
        moods: moods 
      }, { headers: getAuthHeader() });
      
      // Update local state
      setSongs(prev => prev.map(s => s.id === songId ? { ...s, moods: moods } : s));
      setEditingMoods(prev => {
        const newState = { ...prev };
        delete newState[songId];
        return newState;
      });
      
      toast.success("Moods saved!");
    } catch (e) {
      console.error("Failed to save moods:", e);
      toast.error("Failed to save moods");
    } finally {
      setSaving(null);
    }
  };

  // Filter songs
  const filteredSongs = songs.filter(s => {
    if (filter === "with-moods") return s.moods && s.moods.length > 0;
    if (filter === "without-moods") return !s.moods || s.moods.length === 0;
    return true;
  });

  const songsWithMoods = songs.filter(s => s.moods && s.moods.length > 0).length;
  const songsWithoutMoods = songs.length - songsWithMoods;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-4xl animate-bounce">🎵</div>
      </div>
    );
  }

  return (
    <div data-testid="song-manager-page" className="fade-in">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="font-heading text-3xl font-bold text-charcoal mb-2">Song Manager 🎵</h1>
        <p className="text-stone">
          Manage song URLs and mood tags. All URLs are <strong>permanent</strong> and never expire.
        </p>
      </div>

      {/* Stats & Actions */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-charcoal">{songs.length}</div>
            <div className="text-xs text-stone">Total Songs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{songsWithMoods}</div>
            <div className="text-xs text-stone">With Moods</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{songsWithoutMoods}</div>
            <div className="text-xs text-stone">Need Moods</div>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
          >
            <option value="all">All Songs</option>
            <option value="without-moods">Need Moods ({songsWithoutMoods})</option>
            <option value="with-moods">Has Moods ({songsWithMoods})</option>
          </select>
          
          <button
            onClick={copyAllUrls}
            className="bg-sage text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-sage-dark transition-all flex items-center gap-2"
          >
            <Copy size={16} /> Copy All URLs
          </button>
          
          <button
            onClick={repairAudioLinks}
            disabled={repairing}
            className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 transition-all flex items-center gap-2 disabled:opacity-50"
            title="Re-link audio files that lost their connection"
          >
            <Wrench size={16} /> {repairing ? "Repairing..." : "Repair Audio Links"}
          </button>
          
          <button
            onClick={repairNineSongs}
            disabled={repairing}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-all flex items-center gap-2 disabled:opacity-50"
            title="Fix the 9 songs with known audio IDs from support"
          >
            <Wrench size={16} /> {repairing ? "Repairing..." : "Fix 9 Songs (Support IDs)"}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
          <LinkIcon size={18} /> About Permanent Song URLs
        </h3>
        <p className="text-sm text-blue-700 mb-2">
          Each song has a permanent URL that <strong>never expires</strong>:
        </p>
        <code className="block bg-blue-100 px-3 py-2 rounded text-xs text-blue-900 mb-2">
          {SITE_URL}/?song=[song-id]
        </code>
        <p className="text-sm text-blue-700">
          Use these URLs for Paw & Prairie Pulse "Hear the full song" links. They're permanent and public!
        </p>
      </div>

      {/* Song List */}
      <div className="space-y-3">
        {filteredSongs.map((song) => {
          const isExpanded = expandedSong === song.id;
          const currentMoods = editingMoods[song.id] || song.moods || [];
          const hasUnsavedChanges = editingMoods[song.id] !== undefined;
          
          return (
            <div key={song.id} className="bg-white/90 backdrop-blur-sm rounded-xl overflow-hidden border border-white/50">
              {/* Song Header */}
              <div className="p-4 flex items-center gap-4">
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-lg bg-sage/20 flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {song.cover_thumbnail || song.cover_image ? (
                    <img src={song.cover_thumbnail || song.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Music className="text-sage" size={20} />
                  )}
                </div>
                
                {/* Title & Moods */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-charcoal truncate">{song.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {currentMoods.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {currentMoods.slice(0, 3).map(m => {
                          const mood = MOOD_OPTIONS.find(o => o.id === m);
                          return (
                            <span key={m} className="text-xs bg-sage/20 text-sage px-2 py-0.5 rounded-full">
                              {mood?.emoji} {mood?.label || m}
                            </span>
                          );
                        })}
                        {currentMoods.length > 3 && (
                          <span className="text-xs text-stone">+{currentMoods.length - 3} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        No moods set
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyUrl(song.id)}
                    className={`p-2 rounded-lg transition-all ${copiedId === song.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600 hover:bg-sage/20 hover:text-sage'}`}
                    title="Copy permanent URL"
                  >
                    {copiedId === song.id ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                  
                  <a
                    href={`${SITE_URL}/?song=${song.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-all"
                    title="Open song page"
                  >
                    <ExternalLink size={18} />
                  </a>
                  
                  <button
                    onClick={() => setExpandedSong(isExpanded ? null : song.id)}
                    className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-600 transition-all"
                    title="Edit moods"
                  >
                    {isExpanded ? <ChevronUp size={18} /> : <Tag size={18} />}
                  </button>
                </div>
              </div>
              
              {/* Expanded Mood Editor */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                  <div className="text-sm text-stone mb-3">Select mood tags for this song:</div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {MOOD_OPTIONS.map(mood => (
                      <button
                        key={mood.id}
                        onClick={() => toggleMood(song.id, mood.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          currentMoods.includes(mood.id)
                            ? 'bg-sage text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-sage/20'
                        }`}
                      >
                        {mood.emoji} {mood.label}
                      </button>
                    ))}
                  </div>
                  
                  {hasUnsavedChanges && (
                    <button
                      onClick={() => saveMoods(song.id)}
                      disabled={saving === song.id}
                      className="bg-sage text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-sage-dark transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <Save size={16} />
                      {saving === song.id ? "Saving..." : "Save Moods"}
                    </button>
                  )}
                  
                  {/* URL Display */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-stone mb-1">Permanent URL:</div>
                    <code className="text-xs text-charcoal break-all">{SITE_URL}/?song={song.id}</code>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {filteredSongs.length === 0 && (
        <div className="text-center py-12 text-stone">
          No songs found matching your filter.
        </div>
      )}
    </div>
  );
};

export default SongManagerPage;
